import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';

import { solution } from './landingContent';
import { C, FONT, MONO, bandSx, eyebrowSx, h2Sx, sectionSx, wrapSx } from './landingTokens';
import { HEADER_HEIGHT } from './scrollStory';

/**
 * "The solution" as a long-form read: a sticky index on the left (desktop only)
 * and five parts on the right, from the routing model to multi-depot management.
 *
 * The index highlights the part being read and jumps to a part on click. That is
 * the only behaviour; there is no animation.
 */

// A part counts as "being read" once its top passes this share of the window.
const READ_LINE = 0.35;

const SolutionSection: React.FC = () => {
  const partRefs = useRef<(HTMLElement | null)[]>([]);
  const [active, setActive] = useState(0);

  useEffect(() => {
    let frame = 0;
    const update = () => {
      frame = 0;
      const line = window.innerHeight * READ_LINE;
      let current = 0;
      partRefs.current.forEach((el, i) => {
        if (el && el.getBoundingClientRect().top <= line) current = i;
      });
      setActive(current);
    };
    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);

  const jumpTo = (i: number) => (event: React.MouseEvent) => {
    event.preventDefault();
    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    partRefs.current[i]?.scrollIntoView({ behavior: still ? 'auto' : 'smooth', block: 'start' });
  };

  const number = (i: number) => String(i + 1).padStart(2, '0');

  return (
    <Box component="section" id="platform" sx={{ ...sectionSx, ...bandSx }}>
      <Box sx={wrapSx}>
        <Box sx={{ maxWidth: 760 }}>
          <Typography sx={eyebrowSx}>The solution</Typography>
          <Typography component="h2" sx={{ ...h2Sx, mt: 1.5 }}>
            {solution.title}
          </Typography>
        </Box>

        <Box
          sx={{
            mt: { xs: 5, md: 8 },
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '260px minmax(0, 1fr)' },
            gap: { md: 9 },
            alignItems: 'start',
          }}
        >
          {/* Index: desktop only, stays in view while the parts scroll by. */}
          <Box
            component="nav"
            aria-label="The solution, in five parts"
            sx={{ display: { xs: 'none', md: 'block' }, position: 'sticky', top: HEADER_HEIGHT + 32, borderLeft: `1px solid #dfe3ea` }}
          >
            {solution.parts.map((part, i) => {
              const current = i === active;
              return (
                <Box
                  component="a"
                  key={part.id}
                  href={`#${part.id}`}
                  onClick={jumpTo(i)}
                  aria-current={current ? 'true' : undefined}
                  sx={{
                    display: 'flex',
                    gap: 1.5,
                    py: 1.25,
                    pl: 2.25,
                    ml: '-1px',
                    borderLeft: `2px solid ${current ? C.blue : 'transparent'}`,
                    fontFamily: FONT,
                    fontSize: 15,
                    fontWeight: current ? 700 : 500,
                    color: current ? C.ink : C.muted,
                    textDecoration: 'none',
                    transition: 'color .2s ease, border-color .2s ease',
                    '&:hover': { color: C.ink },
                    '&:focus-visible': { outline: `2px solid ${C.blue}`, outlineOffset: 2 },
                  }}
                >
                  <Box component="span" sx={{ fontFamily: MONO, fontSize: 13, fontWeight: 500, color: current ? C.blue : C.subtle }}>
                    {number(i)}
                  </Box>
                  {part.title}
                </Box>
              );
            })}
          </Box>

          {/* The five parts. */}
          <div>
            {solution.parts.map((part, i) => (
              <Box
                component="article"
                key={part.id}
                id={part.id}
                ref={(el: HTMLElement | null) => {
                  partRefs.current[i] = el;
                }}
                aria-labelledby={`${part.id}-title`}
                sx={{
                  scrollMarginTop: `${HEADER_HEIGHT + 32}px`,
                  py: { xs: 4.5, md: 6 },
                  '&:first-of-type': { pt: 0 },
                  '&:last-of-type': { pb: 0 },
                  '&:not(:last-of-type)': { borderBottom: `1px solid ${C.line}` },
                }}
              >
                <Typography sx={{ fontFamily: MONO, fontSize: 12, letterSpacing: '0.1em', color: C.subtle }}>{number(i)}</Typography>
                <Typography
                  component="h3"
                  id={`${part.id}-title`}
                  sx={{ fontFamily: FONT, fontWeight: 800, fontSize: { xs: 22, md: 26 }, letterSpacing: '-0.02em', lineHeight: 1.2, color: C.ink, mt: 1.25 }}
                >
                  {part.title}
                </Typography>
                <Typography sx={{ fontFamily: FONT, fontSize: 16, lineHeight: 1.7, color: C.body, mt: 1.5, maxWidth: 640 }}>
                  {part.body}
                </Typography>
                <Box
                  component="ul"
                  sx={{
                    listStyle: 'none',
                    p: 0,
                    m: 0,
                    mt: 2.75,
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                    columnGap: 4,
                  }}
                >
                  {part.facts.map(fact => (
                    <Box component="li" key={fact} sx={{ py: 1.5, borderTop: `1px solid ${C.line}`, fontFamily: FONT, fontSize: 15, color: C.ink }}>
                      {fact}
                    </Box>
                  ))}
                </Box>
              </Box>
            ))}
          </div>
        </Box>
      </Box>
    </Box>
  );
};

export default SolutionSection;
